import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterBar, type FilterOption } from '@/components/shared/FilterBar';
import {
  useFilterBarSelectAll,
  type FilterBarChangeMeta,
} from '@/components/shared/filterBarSelectAll';

import type { ScopeOption } from '../hooks/useScopeFilterOptions';
import { fetchProductScopeOptions } from '../utils/entityScopeProductOptions';
import {
  EMPTY_ENTITY_SCOPE_FILTERS,
  type EntityScopeFilters,
} from './ConsensusScopeFilters';

interface Props {
  value: EntityScopeFilters;
  onChange: (value: EntityScopeFilters) => void;
  families: ScopeOption[];
  subfamilies?: ScopeOption[];
  warehouses?: ScopeOption[];
  suppliers: ScopeOption[];
  className?: string;
  showProduct?: boolean;
  /** Muestra subfamilia del catálogo (etiqueta «Categoría» en UI). */
  showSubfamily?: boolean;
  showWarehouse?: boolean;
  disabledFilterIds?: string[];
}

function entityToRecord(
  filters: EntityScopeFilters,
  includeWarehouse: boolean,
  includeProduct: boolean,
): Record<string, string[]> {
  const record: Record<string, string[]> = {
    family: [...filters.family_codes],
    subfamily: [...filters.subfamily_codes],
    supplier: [...filters.supplier_codes],
  };
  if (includeWarehouse) {
    record.warehouse = [...filters.warehouse_codes];
  }
  if (includeProduct) {
    record.product = [...filters.product_codes];
  }
  return record;
}

function recordToEntity(
  record: Record<string, string[]>,
  includeWarehouse: boolean,
  includeProduct: boolean,
): EntityScopeFilters {
  return {
    family_codes: record.family ?? [],
    subfamily_codes: record.subfamily ?? [],
    warehouse_codes: includeWarehouse ? (record.warehouse ?? []) : [],
    supplier_codes: record.supplier ?? [],
    product_codes: includeProduct ? (record.product ?? []) : [],
  };
}

function buildActiveFilters(
  filters: EntityScopeFilters,
  labelByCode: {
    family: Map<string, string>;
    subfamily: Map<string, string>;
    warehouse: Map<string, string>;
    supplier: Map<string, string>;
    product: Map<string, string>;
  },
  includeWarehouse: boolean,
  includeProduct: boolean,
): Array<{ id: string; label: string; value: string }> {
  const rows: Array<{ id: string; label: string; value: string }> = [];
  const pairs: Array<['family' | 'subfamily' | 'warehouse' | 'supplier' | 'product', string[]]> = [
    ['family', filters.family_codes],
    ['subfamily', filters.subfamily_codes],
    ['supplier', filters.supplier_codes],
  ];
  if (includeWarehouse) {
    pairs.push(['warehouse', filters.warehouse_codes]);
  }
  if (includeProduct) {
    pairs.push(['product', filters.product_codes]);
  }
  for (const [id, codes] of pairs) {
    for (const code of codes) {
      rows.push({ id, label: labelByCode[id].get(code) ?? code, value: code });
    }
  }
  return rows;
}

export function EntityScopeFilterBar({
  value,
  onChange,
  families,
  subfamilies = [],
  warehouses = [],
  suppliers,
  className = '',
  showProduct = true,
  showSubfamily = false,
  showWarehouse = false,
  disabledFilterIds = [],
}: Props) {
  const { t } = useTranslation();
  const valueRef = useRef(value);
  const [productLabelByCode, setProductLabelByCode] = useState<Map<string, string>>(new Map());
  const {
    selectAllByFilter,
    updateSelectAllFromChange,
    clearSelectAllFor,
    clearAllSelectAll,
  } = useFilterBarSelectAll();

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const productCodesKey = value.product_codes.join(',');

  useEffect(() => {
    if (!showProduct || !productCodesKey) return;
    const codes = productCodesKey.split(',').filter(Boolean);

    let cancelled = false;
    void (async () => {
      const updates = new Map<string, string>();
      await Promise.all(
        codes.map(async (code) => {
          const opts = await fetchProductScopeOptions(code);
          const hit = opts.find((o) => o.code === code);
          if (hit) updates.set(code, hit.label);
        }),
      );
      if (!cancelled && updates.size > 0) {
        setProductLabelByCode((prev) => {
          const next = new Map(prev);
          for (const [code, label] of updates) {
            if (!next.has(code)) next.set(code, label);
          }
          return next;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [productCodesKey, showProduct]);

  const labelByCode = useMemo(
    () => ({
      family: new Map(families.map((f) => [f.code, f.label])),
      subfamily: new Map(subfamilies.map((s) => [s.code, s.label])),
      warehouse: new Map(warehouses.map((w) => [w.code, w.label])),
      supplier: new Map(suppliers.map((s) => [s.code, s.label])),
      product: productLabelByCode,
    }),
    [families, subfamilies, warehouses, suppliers, productLabelByCode],
  );

  const activeFilters = useMemo(
    () => buildActiveFilters(value, labelByCode, showWarehouse, showProduct),
    [value, labelByCode, showWarehouse, showProduct],
  );

  const filterOptions = useMemo((): FilterOption[] => {
    const disabled = new Set(disabledFilterIds);
    const rows: FilterOption[] = [
      {
        id: 'family',
        name: t('scopeFilters.family'),
        options: families.map((f) => ({ value: f.code, label: f.label })),
        multiple: true,
        helpText: t('demandPlanning.help.scopeFamily'),
      },
    ];
    if (showSubfamily) {
      rows.push({
        id: 'subfamily',
        name: t('scopeFilters.category'),
        options: subfamilies.map((s) => ({ value: s.code, label: s.label })),
        multiple: true,
        helpText: t('demandPlanning.help.scopeSubfamily'),
      });
    }
    if (showWarehouse) {
      rows.push({
        id: 'warehouse',
        name: t('scopeFilters.warehouse'),
        options: warehouses.map((w) => ({ value: w.code, label: w.label })),
        multiple: true,
        helpText: t('demandPlanning.help.scopeWarehouse'),
      });
    }
    rows.push({
      id: 'supplier',
      name: t('scopeFilters.supplier'),
      options: suppliers.map((s) => ({ value: s.code, label: s.label })),
      multiple: true,
      helpText: t('demandPlanning.help.scopeSupplier'),
    });
    if (showProduct) {
      rows.push({
        id: 'product',
        name: t('scopeFilters.product'),
        options: [],
        loadOptionsRemotely: true,
        multiple: true,
        enableSelectAll: false,
        helpText: t('demandPlanning.help.scopeProduct'),
      });
    }
    return rows.filter((row) => !disabled.has(row.id));
  }, [
    families,
    subfamilies,
    warehouses,
    suppliers,
    t,
    showSubfamily,
    showWarehouse,
    showProduct,
    disabledFilterIds,
  ]);

  const fetchOptionsForFilter = useCallback(
    async (filterId: string, searchQuery: string) => {
      if (filterId !== 'product') return [];
      const opts = await fetchProductScopeOptions(searchQuery);
      return opts.map((o) => ({ value: o.code, label: o.label }));
    },
    [],
  );

  const handleFilterChange = useCallback(
    (
      newFilters: Record<string, string[]>,
      meta?: FilterBarChangeMeta,
    ) => {
      updateSelectAllFromChange(newFilters, meta);
      if (meta?.selectedLabel?.filterId === 'product') {
        const code = newFilters.product?.at(-1);
        if (code) {
          setProductLabelByCode((prev) =>
            new Map(prev).set(code, meta.selectedLabel!.label),
          );
        }
      }
      const current = valueRef.current;
      const record = entityToRecord(current, showWarehouse, showProduct);
      Object.entries(newFilters).forEach(([key, values]) => {
        if (values.length > 0) record[key] = values;
        else delete record[key];
      });
      onChange(recordToEntity(record, showWarehouse, showProduct));
    },
    [onChange, updateSelectAllFromChange, showWarehouse, showProduct],
  );

  const handleClearFilter = useCallback(
    (filterId: string) => {
      clearSelectAllFor(filterId);
      const current = valueRef.current;
      const record = entityToRecord(current, showWarehouse, showProduct);
      delete record[filterId];
      onChange(recordToEntity(record, showWarehouse, showProduct));
    },
    [onChange, clearSelectAllFor, showWarehouse, showProduct],
  );

  const handleClearAllFilters = useCallback(() => {
    clearAllSelectAll();
    setProductLabelByCode(new Map());
    onChange(EMPTY_ENTITY_SCOPE_FILTERS);
  }, [onChange, clearAllSelectAll]);

  return (
    <FilterBar
      className={className}
      filters={filterOptions}
      activeFilters={activeFilters}
      selectAllByFilter={selectAllByFilter}
      onFilterChange={handleFilterChange}
      onClearFilter={handleClearFilter}
      onClearAllFilters={handleClearAllFilters}
      fetchOptionsForFilter={showProduct ? fetchOptionsForFilter : undefined}
    />
  );
}
